import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";
import { buildQuery } from "../../../utils/buildQuery";

// Dashboard data is scoped to the signed-in user. The store survives a
// sign-out/sign-in without a page reload, so cache entries are keyed by a
// short non-reversible fingerprint of the session token: another user's
// cached months are never reused.
const sessionCacheScope = () => {
  let token = "";
  try {
    token = localStorage.getItem("token") || "";
  } catch (_error) {
    token = "";
  }
  let hash = 0x811c9dc5;
  for (let index = 0; index < token.length; index += 1) {
    hash ^= token.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
};

const scopedQueryArgs = ({ queryArgs, endpointDefinition, endpointName }) =>
  `${sessionCacheScope()}:${defaultSerializeQueryArgs({
    queryArgs, endpointDefinition, endpointName,
  })}`;

export const ShelfAvailabilityApi = createApi({
  reducerPath: "ShelfAvailabilityReport",
  tagTypes: ["LostOpportunityAssumptions"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND2_URL}/v1`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getLostOpportunityOptions: builder.query({
      query: (formId) => `/lost-opportunities/options?formId=${formId}`,
      serializeQueryArgs: scopedQueryArgs,
    }),
    getLostOpportunityAssumptions: builder.query({
      query: (formId) => `/lost-opportunities/assumptions?formId=${formId}`,
      serializeQueryArgs: scopedQueryArgs,
      providesTags: ["LostOpportunityAssumptions"],
    }),
    saveLostOpportunityAssumption: builder.mutation({
      query: (body) => ({ url: "/lost-opportunities/assumptions", method: "PUT", body }),
      invalidatesTags: ["LostOpportunityAssumptions"],
    }),
    getLostOpportunityReport: builder.query({
      query: (params) => `/lost-opportunities/report${buildQuery(params)}`,
      serializeQueryArgs: scopedQueryArgs,
      providesTags: ["LostOpportunityAssumptions"],
    }),
    getRetailDashboard: builder.query({
      query: (params) => {
        const query = buildQuery(params);
        return `/shelf-availability/dashboard${query}`;
      },
      serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) =>
        `${sessionCacheScope()}:${defaultSerializeQueryArgs({
          queryArgs,
          endpointDefinition,
          endpointName,
        })}`,
      // Keep a period cached for ten minutes after its last subscriber goes
      // away, so toggling the month comparison or switching back is instant.
      keepUnusedDataFor: 600,
    }),
    getShelfAvailabilityReport: builder.query({
      query: (params) => {
        const query = buildQuery(params);
        return `/shelf-availability${query}`;
      },
    }),
    getShelfAvailabilityStores: builder.query({
      query: ({ formId }) =>
        `/shelf-availability/filters/stores?formId=${formId}`,
    }),
    getShelfAvailabilityReps: builder.query({
      query: ({ formId }) =>
        `/shelf-availability/filters/reps?formId=${formId}`,
    }),
    getShelfAvailabilityProducts: builder.query({
      query: ({ formId }) =>
        `/shelf-availability/filters/products?formId=${formId}`,
    }),
    /** GET binary export (e.g. xlsx) — same path as MaintBackend shelf-availability/export */
    getShelfAvailabilityExport: builder.query({
      async queryFn({ formId, ...rest } = {}, _queryApi, _extraOptions) {
        const fid = formId ?? process.env.REACT_APP_SHELF_FORM_ID;
        if (!fid) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              data: "Missing formId for shelf availability export",
            },
          };
        }
        // Forward the same filters as the on-screen report so the Excel matches it
        // (date range, store/rep/sku/states, store types and categories).
        const query = buildQuery({ formId: fid, ...rest });
        try {
          const result = await fetch(
            `${process.env.REACT_APP_BACKEND2_URL}/v1/shelf-availability/export${query}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                Accept: "*/*",
              },
            },
          );
          if (!result.ok) {
            return {
              error: { status: result.status, data: await result.text() },
            };
          }
          const blob = await result.blob();
          return { data: blob };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", data: err.message } };
        }
      },
    }),
  }),
});

export const {
  useGetLostOpportunityOptionsQuery,
  useGetLostOpportunityAssumptionsQuery,
  useSaveLostOpportunityAssumptionMutation,
  useLazyGetLostOpportunityReportQuery,
  useGetRetailDashboardQuery,
  useLazyGetShelfAvailabilityReportQuery,
  useLazyGetShelfAvailabilityStoresQuery,
  useLazyGetShelfAvailabilityRepsQuery,
  useLazyGetShelfAvailabilityProductsQuery,
  useLazyGetShelfAvailabilityExportQuery,
} = ShelfAvailabilityApi;
